import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider } from './context/LanguageContext';
import LoadingScreen from './components/LoadingScreen';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Inquiries from './pages/Inquiries';
import Leads from './pages/Leads';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import FollowUps from './pages/FollowUps';
import AIDrafts from './pages/AIDrafts';
import Marketplace from './pages/Marketplace';
import Support from './pages/Support';
import MarketTrends from './pages/MarketTrends';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ResetPassword from './pages/ResetPassword';
import ProductDetail from './pages/ProductDetail';
import BuyerDashboard from './pages/BuyerDashboard';
import SellerProfile from './pages/SellerProfile';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import HowItWorks from './pages/HowItWorks';
import ExportTools from './pages/ExportTools';
import Pricing from './pages/Pricing';
import BuyerSignup from './pages/BuyerSignup';
import TradeSafety from './pages/TradeSafety';
import { BlogList, BlogPost } from './pages/Blog';
import GlobalChatbot from './components/GlobalChatbot';
import BackButton from './components/BackButton';
import AuthCallback from './components/AuthCallback';
import './App.css';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Analytics tracking - generate unique visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Track page views
const trackPageView = async (page) => {
  try {
    await axios.post(`${API}/analytics/track`, {
      event: 'page_view',
      page: page,
      visitor_id: getVisitorId()
    });
  } catch (error) {
    // Silently fail - analytics shouldn't break the app
  }
};

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle suspended accounts
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('suspended')) {
        // Account is suspended - clear local storage and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Show alert and redirect
        alert(detail);
        window.location.href = '/auth';
      }
    }
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children }) {
  const [isAuth, setIsAuth] = useState(null);

  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuth(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/me`);
        setIsAuth(true);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuth(false);
      }
    };

    verifyAuth();
  }, []);

  if (isAuth === null) {
    return <LoadingScreen isLoading={true} minDuration={500} />;
  }

  return isAuth ? children : <Navigate to="/auth" replace />;
}

// Page view tracker component
function PageTracker() {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  
  return null;
}

// Navigation loading handler - shows loading on every route change
function NavigationLoader() {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    // Show loader on route change
    setIsNavigating(true);
    setShowLoader(true);
    
    // Hide loader after a brief moment (page content loads)
    const timer = setTimeout(() => {
      setIsNavigating(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return <LoadingScreen isLoading={isNavigating} minDuration={400} />;
}

function AppContent() {
  return (
    <>
      <NavigationLoader />
      <PageTracker />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/product/:productId" element={<ProductDetail />} />
        <Route path="/support" element={<Support />} />
        <Route path="/contact" element={<Support />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inquiries"
              element={
                <ProtectedRoute>
                  <Inquiries />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/followups"
              element={
                <ProtectedRoute>
                  <FollowUps />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-drafts"
              element={
                <ProtectedRoute>
                  <AIDrafts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/market-trends"
              element={
                <ProtectedRoute>
                  <MarketTrends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/export-tools"
              element={
                <ProtectedRoute>
                  <ExportTools />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buyer-dashboard"
              element={
                <ProtectedRoute>
                  <BuyerDashboard />
                </ProtectedRoute>
              }
            />
            {/* Seller Profile Route */}
            <Route
              path="/seller/:sellerId"
              element={
                <ProtectedRoute>
                  <SellerProfile />
                </ProtectedRoute>
              }
            />
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            {/* Password Reset Route */}
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Google OAuth Callback */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            {/* Legal Pages - Public */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            {/* How It Works - Public */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            {/* Pricing - Public */}
            <Route path="/pricing" element={<Pricing />} />
            {/* Buyer Verification - Public */}
            <Route path="/buyer-signup" element={<BuyerSignup />} />
            <Route path="/buyer-verification" element={<BuyerSignup />} />
            {/* Blog - Public */}
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Trade Safety Guide - Public */}
            <Route path="/trade-safety" element={<TradeSafety />} />
          </Routes>
          {/* Global Chatbot - Visible on all pages */}
          <GlobalChatbot />
          {/* Global Back Button - Visible on all pages except Landing */}
          <BackButton />
        </>
      );
    }

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
        <Toaster />
      </div>
    </LanguageProvider>
  );
}

export default App;
